/**
 * useEdgeSwipe — natural mobile gesture for opening / closing the sidebar.
 *
 * onOpenSwipe : right-swipe starting near the LEFT edge (within edgeZone px)
 * onCloseSwipe: left-swipe anywhere on the bound element
 *
 * Thresholds tuned for finger ergonomics (Android Chrome, iOS Safari):
 *  - min horizontal distance : 60px
 *  - max vertical drift      : 80px (rejects accidental scroll)
 *  - max gesture duration    : 600ms (rejects slow drags)
 *
 * No external dep. Use a single hook in SaaSLayout that wires both edges.
 */
import { useEffect } from "react";

export function useEdgeSwipe({ enabled = true, edgeZone = 28, onOpenSwipe, onCloseSwipe }) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    let startX = 0, startY = 0, startT = 0, fromEdge = false, tracking = false;

    const onTouchStart = (e) => {
      const t = e.touches[0];
      if (!t) return;
      startX = t.clientX; startY = t.clientY; startT = Date.now();
      fromEdge = t.clientX <= edgeZone;
      tracking = true;
    };
    const onTouchEnd = (e) => {
      if (!tracking) return;
      tracking = false;
      const t = (e.changedTouches && e.changedTouches[0]) || null;
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      const dt = Date.now() - startT;
      if (dt > 600 || dy > 80) return;
      if (dx >= 60 && fromEdge && onOpenSwipe) onOpenSwipe();
      else if (dx <= -60 && onCloseSwipe) onCloseSwipe();
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [enabled, edgeZone, onOpenSwipe, onCloseSwipe]);
}
